/**
 * Item actions ported from reware-admin's Inventory Items portal
 * (inventory-items-api.js / app-api.js). Each action hits the same
 * admin_graphql mutation the admin portal uses.
 */
import { execAdmin } from '@src/apis/reware/utils/exec';

export const acceptItem = async (id: number): Promise<void> => {
  const query = `
    mutation acceptInventoryItem($id: Int!) {
      inventoryItem(id: $id) {
        accept {
          id
          state
        }
      }
    }
  `;
  await execAdmin<{ id: number }, unknown>(query, { variables: { id } });
};

export const reviewItem = async (id: number): Promise<void> => {
  const query = `
    mutation reviewInventoryItem($id: Int!) {
      inventoryItem(id: $id) {
        review {
          id
          state
        }
      }
    }
  `;
  await execAdmin<{ id: number }, unknown>(query, { variables: { id } });
};

export const rejectItem = async (
  id: number,
  reason: string,
  note?: string
): Promise<void> => {
  const query = `
    mutation rejectInventoryItem($id: Int!, $reason: String!, $note: String) {
      inventoryItem(id: $id) {
        reject(reason: $reason, note: $note) {
          id
          state
          rejectedReason
        }
      }
    }
  `;
  await execAdmin<{ id: number, reason: string, note?: string }, unknown>(query, {
    variables: { id, reason, note },
  });
};

export interface UpdateItemInput {
  id: number;
  /** Price in dollars; converted to cents like the admin portal does */
  price: number;
  condition: string;
  notes: string;
}

export const updateItem = async ({ id, price, condition, notes }: UpdateItemInput): Promise<void> => {
  const cents = Math.round(price * 100);
  const query = `
    mutation updateInventoryItem(
      $id: Int!
      $price: Int!
      $condition: String!
      $notes: String!
    ) {
      inventoryItem(id: $id) {
        update(price: $price, condition: $condition, notes: $notes) {
          id
          price
          condition
          notes(omitMeasurements: ALWAYS)
        }
      }
    }
  `;
  await execAdmin<{ id: number, price: number, condition: string, notes: string }, unknown>(
    query,
    { variables: { id, price: cents, condition, notes } }
  );
};

export const printItemLabel = async (itemId: number): Promise<void> => {
  const query = `
    mutation printItemLabel($itemId: Int!) {
      inventoryItem(id: $itemId) {
        printLabel {
          result
        }
      }
    }
  `;
  await execAdmin<{ itemId: number }, unknown>(query, { variables: { itemId } });
};

export const printItemHangtagLabel = async (itemId: number): Promise<void> => {
  const query = `
    mutation printItemHangtagLabel($itemId: Int!) {
      inventoryItem(id: $itemId) {
        printHangtagLabel {
          result
        }
      }
    }
  `;
  await execAdmin<{ itemId: number }, unknown>(query, { variables: { itemId } });
};

export const applyCatalogPhotography = async (itemId: number): Promise<void> => {
  const query = `
    mutation useCatalogPhotography($itemId: Int!) {
      inventoryItem(id: $itemId) {
        useCatalogPhotography {
          id
          state
        }
      }
    }
  `;
  await execAdmin<{ itemId: number }, unknown>(query, { variables: { itemId } });
};

export const reshootLikeNewPhotography = async (itemId: number): Promise<void> => {
  const query = `
    mutation reshootLikeNewPhotography($itemId: Int!) {
      inventoryItem(id: $itemId) {
        reshootLikeNewPhotography {
          id
        }
      }
    }
  `;
  await execAdmin<{ itemId: number }, unknown>(query, { variables: { itemId } });
};

export const syncShopifyCatalogProduct = async (
  partnerCode: string,
  parentSku: string
): Promise<string> => {
  const query = `
    mutation SyncShopifyCatalogProduct($partnerCode: String!, $parentSku: String!) {
      syncShopifyCatalogProduct(partnerCode: $partnerCode, parentSku: $parentSku) {
        message
      }
    }
  `;
  const data = await execAdmin<
    { partnerCode: string, parentSku: string },
    { syncShopifyCatalogProduct: { message: string } }
  >(query, { variables: { partnerCode, parentSku } });
  return data.syncShopifyCatalogProduct.message;
};
